import {
    ServiceQueries,
    query,
    serviceQueries,
    ServiceEvents,
    ServiceCommands,
    events,
    on,
    emit
} from '@estee/elc-service-bus';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { ViewController } from '../../ViewController';
import { ServiceBusController } from '../ServiceBusController';
import { BaseService } from '../BaseService';
import { IAppConfig } from '@estee/elc-universal-utils';

type diContainerType = { get<T>(key: string): T };
export type ContainerImport = () => Promise<{ diContainer: diContainerType }>;

export type ServiceBusControllerBody = ServiceBusController & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: Promise<(...args: any[]) => any> | Function | any;
} & { onEcommStarted(): void };

interface IServiceStartParams {
    serviceName: string;
    diContainerImport: ContainerImport;
    serviceKey: string;
    serviceBusControllerKey?: string;
    serviceBus?: {
        serviceBusBaseClass?: ServiceBusControllerBody;
        createServiceBusMethod?(): void;
    }
}

export class ServiceManagerClass {
    public serviceName: string;
    private containerImport: ContainerImport;
    private diContainer: diContainerType;
    private serviceKey: string;
    private service: BaseService;
    private serviceBusControllerKey?: string;
    private serviceBusBaseClass?: ServiceBusControllerBody;
    private logger: ELCLogger;
    private ecommStarted: boolean = false;
    private startPromise: Promise<void>;

    get serviceHasStarted(): boolean {
        return Boolean(this.service && this.service.isStarted);
    }

    getContainer = async () => {
        if (!this.diContainer) {
            this.diContainer = (await this.containerImport()).diContainer;
        }

        return this.diContainer;
    };

    handleServiceBusError = () => {
        throw new Error(`
            ServiceBus hasn't been provided to ServiceManager at setServiceStartParams method.
            ServiceBus: ${this.serviceBusBaseClass ? 'ServiceBus found' : this.serviceBusBaseClass}.
            ServiceBusKey: (${this.serviceBusControllerKey})
        `);
    };

    getServiceBusController = async () => {
        let serviceBus;
        try {
            await this.startService();

            const diContainer = await this.getContainer();

            serviceBus = diContainer.get<ServiceBusController>(
                this.serviceBusControllerKey as string
            );

            if (!serviceBus) {
                throw new Error('ServiceBusController not found inside diContainer');
            }
        } catch (error) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: error.message,
                payload: { error }
            });
        }

        return serviceBus as ServiceBusController;
    };

    setServiceStartParams = ({
        serviceName,
        diContainerImport,
        serviceKey,
        serviceBusControllerKey,
        serviceBus
    }: IServiceStartParams) => {
        this.serviceName = serviceName;
        this.containerImport = diContainerImport;
        this.serviceKey = serviceKey;
        this.serviceBusControllerKey = serviceBusControllerKey;
        this.logger = new ELCLogger({ serviceName });
        this.serviceBusBaseClass = serviceBus?.serviceBusBaseClass;
        serviceBus?.createServiceBusMethod?.();
    };

    getServiceBusControllerMethod = (functionName: string) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (...args: any[]) => {
            const srvBusController = await this.getServiceBusController();

            const method = (srvBusController as ServiceBusControllerBody)[functionName] as Function;

            return method.call(null, ...args);
        };

    setDecoratorOnFunction = (decorator: Function) => {
        return ([triggerKey, functionName]: [
            triggerKey: keyof ServiceQueries | keyof ServiceEvents | keyof ServiceCommands,
            functionName: string
        ]) => {
            if (!this.serviceBusBaseClass || !this.serviceBusControllerKey) {
                this.handleServiceBusError();
            } else {
                decorator(triggerKey)(this.serviceBusBaseClass.__proto__, functionName);
                this.serviceBusBaseClass[functionName] = this.getServiceBusControllerMethod(
                    functionName
                );
            }
        };
    };

    setDecoratorOnProperty = (decorator: Function) => {
        return ([keyName, queryName]: [keyName: string, queryName?: string]) => {
            if (!this.serviceBusBaseClass || !this.serviceBusControllerKey) {
                this.handleServiceBusError();
            } else {
                decorator(queryName)(this.serviceBusBaseClass.__proto__, keyName);

                Object.defineProperty(this.serviceBusBaseClass.__proto__, keyName, {
                    configurable: true,
                    enumerable: true,
                    set(value) {
                        return this.getServiceBusController().then(
                            (srvBusController: ServiceBusController) => {
                                // @ts-ignore
                                srvBusController[keyName] = value;
                            }
                        );
                    }
                });
            }
        };
    };

    @on(events.ECOMM_STARTED, { replay: true })
    public setEcommHasStarted = () => {
        this.ecommStarted = true;
    };

    onStart = (cb: (error?: Error) => void, ecommConfig?: IAppConfig) => async () => {
        let error: Error | undefined;
        try {
            if (this.serviceHasStarted) {
                cb();

                return;
            }
            if (!ecommConfig && !this.ecommStarted) {
                return;
            }

            await this.getContainer();
            this.service = this.diContainer.get<BaseService>(this.serviceKey);

            let serviceConfig = ecommConfig;

            if (!serviceConfig) {
                const config = (await query(serviceQueries.GET_CONFIG, ['ecommConfig'])) as
                    | { ecommConfig?: IAppConfig }
                    | undefined;

                // If ecommConfig is not available then EcommApp hasn't started yet
                if (!config || !config.ecommConfig) {
                    throw new Error('EcommConfig not found');
                }

                serviceConfig = config.ecommConfig;
            }

            await this.service.start(serviceConfig);
            emit(events.SERVICE_STARTED, { serviceName: this.serviceName });
        } catch (err) {
            error = err;
            this.logger.error({
                triggerType: TriggerType.render,
                message: err.message,
                payload: {
                    error: err
                }
            });
        } finally {
            cb(error);
        }
    };

    startService = (ecommConfig?: IAppConfig) => {
        if (!this.startPromise) {
            this.startPromise = new Promise((resolve, reject) => {
                let timeoutId: NodeJS.Timeout | null = null;
                timeoutId = setInterval(
                    this.onStart((error?: Error) => {
                        if (timeoutId) {
                            clearInterval(timeoutId);
                        }
                        if (error) {
                            reject(error);
                        } else {
                            resolve();
                        }
                    }, ecommConfig),
                    200
                );
            });
        }

        return this.startPromise;
    };

    getController = (controllerKey?: string): ViewController | undefined => {
        if (!this.diContainer) {
            const message = "Can't request controller without starting the service first";

            this.logger.error({
                triggerType: TriggerType.render,
                message,
                payload: {
                    serviceName: this.serviceName,
                    context: {
                        method: 'getController method on ServiceManager'
                    }
                }
            });

            throw new Error(message);
        }

        const controller = controllerKey
            ? this.diContainer.get<ViewController>(controllerKey)
            : undefined;

        return controller;
    };
}

export const ServiceManager = new ServiceManagerClass();
